import {Injectable} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, Observable, finalize, map, shareReplay, switchMap, tap} from 'rxjs';
import {DialogService} from 'src/app/services/dialog.service';
import {LpInvestingEntityDataService} from 'src/app/services/lp/lp-investing-entity-data.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {ConfirmDialogParams} from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import {InvestingEntityShareComponent} from '../investing-entity-share.component';

@Injectable({providedIn: 'root'})
export class InvestingEntityShareService {
  constructor(
    private readonly investingEntityDataService: LpInvestingEntityDataService,
    private readonly dialogService: DialogService,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService
  ) { }

  private reload = new BehaviorSubject(null);

  private ModalRef: MatDialogRef<InvestingEntityShareComponent, any>;

  allInvestingEntities$: Observable<any> =
    this.investingEntityDataService.getProtfolioInvestingEntitiesByCurrentClient().pipe(shareReplay(1));

  investingEntities$: Observable<any> = this.allInvestingEntities$.pipe(map(entities => entities.filter(entity => entity.type === "Owner")));

  sharedEntities$: Observable<any> = this.reload.pipe(
    switchMap(() => {
      return this.allInvestingEntities$.pipe(
        map(entities => entities.filter(entity => entity.type === "OwnerSharing").reduce((acc: any[], val) => {
          const groupedInvestingEntity = acc.find(gie => gie.user.email === val.email && gie.user.id === val.userId)
          if (groupedInvestingEntity) {
            groupedInvestingEntity.investingEntities.push(val);
            return acc;
          }
          acc.push({user: {email: val.email, id: val.userId, name: val.userName}, investingEntities: [val]})
          return acc
        }, []))
      );
    })
  );

  openModal() {
    this.ModalRef = this.dialog.open(InvestingEntityShareComponent, {
      panelClass: 'lp-mobile-full-screen-dialog',
      backdropClass: 'lp-mobile-full-screen-dialog-backdrop',
    });

    this.ModalRef.afterClosed().subscribe();
  }

  share(email: string, investingEntitiesIds: number[]) {
    this.investingEntityDataService
      .createSharedEntitiesForUserByCurrentClient(email, investingEntitiesIds)
      .pipe(
        tap(() => this.reload.next(null)),
        finalize(() => this.ModalRef?.close())
      )
      .subscribe((res) => {
        if (res) {
          this.snackbarService.showGeneralMessage(`${res}`); // can get error message
        } else {
          this.snackbarService.showGeneralMessage('Access updated');
        }
      });
  }

  update(email: string, investingEntitiesIds: number[]) {
    this.investingEntityDataService
      .updateAccessForUserByCurrentClient(email, investingEntitiesIds)
      .pipe(
        tap(() => this.reload.next(null)),
        finalize(() => this.ModalRef?.close())
      )
      .subscribe((res) =>
        this.snackbarService.showGeneralMessage('Access updated')
      );
  }

  remove(email: string) {
    this.investingEntityDataService
      .removeAccessForUserByCurrentClient(email)
      .pipe(tap(() => this.reload.next(null)))
      .subscribe((res) =>
        this.snackbarService.showGeneralMessage('Access removed')
      );
  }

  resend(email: string) {
    this.investingEntityDataService
      .resendSharedInvitation(email)
      .subscribe((res) =>
        this.snackbarService.showGeneralMessage('Invite sent')
      );
  }

  confirm(email: string, name?: string) {
    this.ModalRef?.close();

    const dialogParams = new ConfirmDialogParams();
    dialogParams.panelClass = 'red-action-button';
    dialogParams.title = `Remove Access`;
    dialogParams.description = `Are you sure you want to revoke access for ${name ? name : email
      }? \nThis action will block any future access to your deals.`;
    dialogParams.actionLabel = 'Remove';
    dialogParams.actionPosition = 'right';

    const dialogRef = this.dialogService.confirmDialog(dialogParams);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.remove(email);
      }
      this.openModal();
    });
  }
}
